<template>
  <div class="card mb-4">
    <TableInfoBar
      :total="totalItems"
      v-model:pageSize="pageSize"
      :page-sizes="pageSizes"
      create-label="제품정보 등록"
      v-model:query="q"
      :searchPlaceholder="'이름/GTIN 검색'"
      @search="onSearch"
      @create="createData"
    />

    <div class="card-body px-4 pt-0 pb-2">
      <BaseDataTable
        :loading="loading"
        :items="productList"
        :columns="columns"
        :total="totalItems"
        v-model:page="page"
        v-model:pageSize="pageSize"
        :page-sizes="pageSizes"
        :colgroup="colgroup"
        :rowKey="rowKey"
        empty-message="등록된 제품이 없습니다."
        @row-click="onRowClick"
      >
        <template #row="{ item, index }">
          <td class="text-center text-sm">{{ totalItems - rowStartIndex - index }}</td>
          <td class="text-center text-sm">{{ item.name }}</td>
          <td class="text-center text-sm">
            <CustomBadge level="very-high" size="sm" :label="item.gtin" />
            <!-- <soft-badge color="info" variant="gradient" size="sm">{{ item.gtin }}</soft-badge> -->
          </td>
          <td class="text-center text-sm">{{ categoryMap[item.category] }}</td>
          <td class="text-center text-sm">{{ formatDate(item.createdAt) }}</td>
          <td class="text-center text-sm">{{ item.updatedAt ? formatDate(item.updatedAt) : '-' }}</td>
        </template>
      </BaseDataTable>
    </div>
  </div>

  <ProductTour />
</template>

<script>
import CustomBadge from '@/components/CustomBadge.vue';
import BaseDataTable from '@/components/BaseDataTable.vue';
import TableInfoBar from '@/components/pagination/TableInfoBar.vue';
import SoftBadge from "@/components/SoftBadge.vue";
import SoftButton from "@/components/SoftButton.vue";
import ProductTour from "@/components/tour/ProductTour.vue";
import api from '@/api/api';

export default {
  name: "product-table",
  components: { 
    CustomBadge,
    SoftButton,
    SoftBadge,
    BaseDataTable,
    TableInfoBar,
    ProductTour
  },
  data() {
    return {
      loading: false,
      categoryList: [],
      manufacturerList: [],
      unitList: [],
      epcList: [],

      productList: [],

      page: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50],
      totalItems: 0,
      sort: 'createdAt',

      columns: [
        { label: 'No.' },
        { label: '제품명' },
        { label: 'GTIN' },
        { label: '분류' },
        { label: '생성일시' },
        { label: '수정일시' },
      ],
      colgroup: [
        'width:10%',
        'width:20%',
        'width:20%',
        'width:10%',
        'width:20%',
        'width:20%',
      ],
      q: "",
    };
  },
  computed: {
    mfId() {
      return this.$store.getters['UserStore/getMfId'];
    },
    categoryMap() {
      const map = {};
      (this.categoryList || []).forEach(dc => {
        // categoryList 요소 구조에 맞게 매핑
        // 예: { categoryCode, categoryName }
        map[dc.categoryCode] = dc.categoryName;
      });
      return map;
    },
    rowStartIndex() {
      return (this.page - 1) * this.pageSize;
    },
  },
  watch: {
    page() { this.fetchData(); },
    pageSize() { this.fetchData(); },
  },
  mounted() { 
    this.initData();
    const { page, size, q, sort } = this.$route.query;
    this.updatingFromRoute = true;  
    if (page) this.page = +page;
    if (size) this.pageSize = +size;
    if (q !== undefined) this.q = q;
    if (sort) this.sort = sort;
    this.updatingFromRoute = false;
    this.fetchData(); 
  },
  methods: {
    rowKey(item, idx) { return item.id ?? idx; },

    async initData() {
      try {
        const { data } = await api.get(`/product/initData`, { params: { mfId: this.mfId } });
        const result = data?.result || {};
        this.categoryList = result.categoryList || [];
        this.manufacturerList = result.manufacturerList || [];
        this.unitList = result.unitList?.detailCodes || result.unitList || [];
        this.epcList = result.epcList || ["SGTIN"];
      } catch (error) {
        console.error("초기 데이터 불러오기 실패:", error);
      }
    },
    async fetchData() {
      if(this.loading) return;

      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get('/product/page', {
            params: { mfId: this.mfId, page: this.page, size: this.pageSize, sort: this.sort, query: this.q  }
          }),
          new Promise(r => setTimeout(r, 600))
        ]);

        const r = res.data?.result || {};
        this.productList = r.items || [];
        this.totalItems = Number(r.total || 0);

        const pageCount = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
        if (this.page > pageCount) this.page = pageCount;
      } catch (error) {
        console.error("제품 목록 불러오기 실패:", error);
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchData();
    },
    async initData() {
      try {
        const { data } = await api.get(`/product/initData`, { params: { mfId: this.mfId } });
        const result = data?.result || {};

        // 서버 스키마에 맞게 보정
        this.categoryList = result.categoryList || [];
        this.manufacturerList = result.manufacturerList || []; // 서버가 제공하면 사용
        this.unitList = result.unitList?.detailCodes || result.unitList || [];
        this.epcList = result.epcList || ['SGTIN'];
      } catch (error) {
        console.error("초기 데이터 불러오기 실패:", error);
      }
    },
    onRowClick(item, index){
      this.$router.push({
        name: "ProductEdit",
        params: { id: item.id },
        query: {
          page: this.page,
          size: this.pageSize,
          q: this.q,
          sort: this.sort,
        },
      });
    },
    createData(){
      this.$router.push({
        name: "ProductCreate",
      });
    }
  },
};
</script>
